import { buildPrefillSubmissionContext, EMPTY_PREFILL_SUBMISSION_CONTEXT, isPrefillMention, resolvePrefillSubmissionContext, serializePrefillMention, } from '#pplx/frontend/apps/ask/ask-input/prefills/prefills.js';
import { getMentionLinkMarkdown } from '#pplx/frontend/composer-sdk/lexical/utils/getMentionLinkMarkdown.js';
import { getSerializedTextContent, shouldSerializeLinkNodeAsPlainText, } from '#pplx/frontend/composer-sdk/serializedNodeText.js';
const extractMentionedUuids = (rootNode, variant) => {
    const uuids = [];
    if (!rootNode) {
        return uuids;
    }
    const processNode = (node) => {
        if (node.type === 'mention') {
            const mentionNode = node;
            const props = mentionNode.props;
            if (props.variant === variant) {
                uuids.push(props.uuid);
            }
        }
        if ('children' in node && Array.isArray(node.children)) {
            node.children.forEach(processNode);
        }
    };
    processNode(rootNode);
    return uuids;
};
export const extractMentionedSources = (rootNode) => extractMentionedUuids(rootNode, 'sources');
export const extractMentionedDevices = (rootNode) => extractMentionedUuids(rootNode, 'device');
export const getQueryWithoutSourceMentions = (rootNode, fallbackQuery) => {
    if (!rootNode) {
        return fallbackQuery;
    }
    let hasSourceMention = false;
    const getNodeText = (node) => {
        if (node.type === 'text' && 'text' in node) {
            return node.text;
        }
        if (node.type === 'linebreak') {
            return '\n';
        }
        if (node.type === 'mention') {
            const props = node.props;
            if (props.variant === 'sources') {
                hasSourceMention = true;
                return '';
            }
            return props.queryText ?? props.text;
        }
        if ('children' in node && Array.isArray(node.children)) {
            return node.children.map(getNodeText).join('');
        }
        return '';
    };
    const query = getNodeText(rootNode).replace(/\s+/g, ' ').trim();
    return hasSourceMention ? query : fallbackQuery;
};
const extractLeadingSlashCommandProps = (rootNode) => {
    if (!rootNode) {
        return undefined;
    }
    let found;
    let blocked = false;
    const processNode = (node) => {
        if (found !== undefined || blocked) {
            return;
        }
        if (node.type === 'text' && 'text' in node) {
            if (typeof node.text === 'string' && node.text.trim().length > 0) {
                blocked = true;
            }
            return;
        }
        if (node.type === 'mention') {
            const props = node.props;
            if (props.variant === 'slash-command') {
                found = props;
                return;
            }
            blocked = true;
            return;
        }
        if ('children' in node && Array.isArray(node.children)) {
            for (const child of node.children) {
                processNode(child);
                if (found !== undefined || blocked)
                    return;
            }
        }
    };
    processNode(rootNode);
    return found;
};
export const extractLeadingSlashCommand = (rootNode) => extractLeadingSlashCommandProps(rootNode)?.uuid;
export const extractLeadingSlashCommandSubmittedLabel = (rootNode) => extractLeadingSlashCommandProps(rootNode)?.slashCommandSubmittedLabel;
export const extractMentionedTabs = (rootNode) => {
    const tabs = [];
    if (!rootNode) {
        return tabs;
    }
    const processNode = (node) => {
        if (node.type === 'mention') {
            const mentionNode = node;
            const props = mentionNode.props;
            if (props.variant === 'tab') {
                tabs.push({
                    id: props.uuid,
                    url: props.url ?? '',
                    title: props.text,
                    faviconUrl: props.icon?.type === 'image' ? props.icon.url : undefined,
                });
            }
        }
        if ('children' in node && Array.isArray(node.children)) {
            node.children.forEach(processNode);
        }
    };
    processNode(rootNode);
    return tabs;
};
export const convertLexicalJsonToQuery = (rootNode) => {
    if (!rootNode) {
        return { dslQuery: undefined, mentions: [], ...EMPTY_PREFILL_SUBMISSION_CONTEXT };
    }
    const mentions = [];
    const prefills = [];
    let citationIndex = 0;
    const processChildren = (children) => {
        const text = children.map(getSerializedTextContent).join('');
        let startOffset = 0;
        return children
            .map((child) => {
            const childText = getSerializedTextContent(child);
            const context = {
                endOffset: startOffset + childText.length,
                startOffset,
                text,
            };
            startOffset = context.endOffset;
            return processNode(child, context);
        })
            .join('');
    };
    const processNode = (node, context) => {
        let result = '';
        if (node.type === 'text' && 'text' in node) {
            result = node.text;
        }
        else if (node.type === 'linebreak') {
            result = '\n';
        }
        else if ((node.type === 'link' || node.type === 'autolink') && 'url' in node) {
            // match the markdown link syntax
            const text = ('children' in node && Array.isArray(node.children) && processChildren(node.children)) ||
                '';
            if (shouldSerializeLinkNodeAsPlainText({ node, context })) {
                return text;
            }
            const title = 'title' in node ? node.title : undefined;
            result = getMentionLinkMarkdown(text, node.url, title) ?? text;
            // children already processed
            return result;
        }
        else if (node.type === 'mention') {
            const mentionNode = node;
            const props = mentionNode.props;
            if (props.variant === 'tab') {
                mentions.push({
                    id: props.uuid,
                    url: props.url ?? '',
                    type: props.variant,
                });
                citationIndex++;
                result = `<attachment citation_id="${citationIndex}" />`;
                // no children
                return result;
            }
            else if (props.variant === 'shortcut') {
                mentions.push({
                    id: props.uuid,
                    url: '',
                    type: props.variant,
                });
                result = props.queryText ?? '';
                // no children
                return result;
            }
            else if (isPrefillMention(props)) {
                const serialized = serializePrefillMention(props);
                mentions.push(serialized.mention);
                prefills.push(serialized.prefill);
                return serialized.text;
            }
            else if (props.variant === 'user') {
                if (!props.url) {
                    return props.text;
                }
                mentions.push({
                    id: props.uuid,
                    url: props.url,
                    type: props.variant,
                });
                // User mentions submit as the pplx://mention markdown DSL so the
                // thread view (and the model) can render/resolve the person chip.
                result = getMentionLinkMarkdown(props.text, props.url) ?? props.text;
                // no children
                return result;
            }
            else if (props.variant === 'sources' ||
                props.variant === 'space' ||
                props.variant === 'device') {
                mentions.push({
                    id: props.uuid,
                    url: '',
                    type: props.variant,
                });
                result = props.text;
                // no children
                return result;
            }
        }
        if ('children' in node && Array.isArray(node.children)) {
            return result + processChildren(node.children);
        }
        return result;
    };
    return {
        dslQuery: processNode(rootNode) || undefined,
        mentions,
        ...buildPrefillSubmissionContext(prefills),
    };
};
/**
 * Resolves the semantic fields submitted alongside an Ask editor draft.
 *
 * `dslQuery` always comes from `rootNode`. The other fields prefer an explicit
 * value and fall back to the value derived from `rootNode` only when that value
 * is `undefined`; an empty array or string is therefore an intentional override.
 * Without a root, the derived query and collections are empty and the slash
 * command fields are undefined.
 */
export function resolveLexicalSubmissionContext({ rootNode, mentions: explicitMentions, slashCommand: explicitSlashCommand, slashCommandSubmittedLabel: explicitSlashCommandSubmittedLabel, ...explicitPrefills }) {
    const { dslQuery, mentions, ...derivedPrefills } = convertLexicalJsonToQuery(rootNode);
    return {
        dslQuery,
        mentions: explicitMentions === undefined
            ? mentions
            : explicitMentions.map((mention) => ({ ...mention })),
        ...resolvePrefillSubmissionContext(derivedPrefills, explicitPrefills),
        slashCommand: explicitSlashCommand ?? extractLeadingSlashCommand(rootNode),
        slashCommandSubmittedLabel: explicitSlashCommandSubmittedLabel ?? extractLeadingSlashCommandSubmittedLabel(rootNode),
    };
}
//# sourceMappingURL=lexicalNodeToQuery.js.map